"use client";

import React, { useState } from "react";
import { Box, IconButton, Typography, type Theme } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useLocale } from "next-intl";
import { formatCurrency } from "@configs/currencies";
import { LIGHT_TOKENS } from "src/themes/tokens";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { useActiveCashback } from "src/hooks/useCashback";
import { bannerCopy } from "src/utils/cashback";
import { shortDateLabel } from "./note-lines";
import { useShopCurrency, useShopTimeZone } from "./use-client-value";

const DISMISS_KEY = "cashbackBannerDismissed:";

export function isBannerDismissed(promotionId: string): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY + promotionId) === "1";
  } catch {
    return false;
  }
}

function dismissBanner(promotionId: string): void {
  try {
    window.sessionStorage.setItem(DISMISS_KEY + promotionId, "1");
  } catch {
    /* storage blocked: the banner just stays closed until the next page */
  }
}

/**
 * The strip's colours. In dark mode the AppBar itself stays light (#f5f5f5
 * with #1a2a35 text, see src/components/header), so the strip follows the bar
 * rather than the dark page: the light tokens, on the bar's own grey.
 */
function bannerColours(theme: Theme) {
  if (theme.palette.mode !== "dark") {
    const tk = theme.tokens;
    return {
      bg: tk.paper,
      ink: tk.ink,
      ink2: tk.ink2,
      ink3: tk.ink3,
      brand: tk.brand,
      rule: tk.mist,
    };
  }
  const tk = LIGHT_TOKENS;
  return {
    bg: "#f5f5f5",
    ink: "#1a2a35",
    ink2: tk.ink2,
    ink3: tk.ink3,
    brand: tk.brand,
    rule: tk.mist,
  };
}

/**
 * The akcija strip directly under the shop header's toolbar, on every shop
 * page: "Spend €40, get €4 back." with the rate and end date below.
 *
 * Quiet on purpose. A paper strip with a hairline under it and the brand teal
 * only on the icon: the orange `action` belongs to the buy button, and the
 * brand is never a full-width wash (see src/themes/tokens.ts).
 *
 * On phones only the headline shows (about 34px); the rate and end date join
 * it from `sm` up.
 *
 * Renders nothing — no height at all — while the switch is off, no promotion
 * runs, or the customer closed it this session. The currency is read from the
 * host only on the client, so the server and the hydrating render agree (both
 * null) and the query starts after hydration.
 */
export default function CashbackBanner() {
  const t = useAppTranslate();
  const locale = useLocale();
  const currency = useShopCurrency();
  const timeZone = useShopTimeZone();
  const [closedId, setClosedId] = useState<string | null>(null);

  const { data } = useActiveCashback(currency);
  if (!data?.active) return null;
  // `data` only exists client-side (the query is disabled until the currency
  // is known), so reading sessionStorage here cannot cause a hydration gap.
  if (closedId === data.promotionId || isBannerDismissed(data.promotionId))
    return null;

  const fmt = (cents: number) => formatCurrency(cents / 100, data.currency);
  const [headline, detail] = bannerCopy(
    data,
    fmt,
    locale,
    // "Akcija velja do {{end}}." ends on the date, and the date is the LAST
    // DAY that earns, in the shop market's zone (stage walk B3): an end at
    // 7. 10. 00:00 reads "do 6. 10." for every viewer, wherever they are.
    shortDateLabel(data.endsAt, locale, {
      endsSentence: true,
      lastDay: true,
      timeZone,
    }),
  );

  return (
    <Box
      role="region"
      aria-label={t(headline.key, headline.params)}
      data-testid="cashback-banner"
      sx={(theme) => ({
        bgcolor: bannerColours(theme).bg,
        color: bannerColours(theme).ink,
        borderBottom: `1px solid ${bannerColours(theme).rule}`,
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 2, sm: 3 },
          py: { xs: 0.5, sm: 0.75 },
        }}
      >
        <AccountBalanceWalletOutlinedIcon
          aria-hidden
          sx={(theme) => ({
            fontSize: { xs: 20, sm: 22 },
            color: bannerColours(theme).brand,
            flexShrink: 0,
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={(theme) => ({
              fontSize: { xs: 13.5, sm: 14 },
              fontWeight: 700,
              lineHeight: 1.35,
              color: bannerColours(theme).ink,
            })}
          >
            {t(headline.key, headline.params)}
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              display: { xs: "none", sm: "block" },
              fontSize: 12.5,
              lineHeight: 1.4,
              color: bannerColours(theme).ink2,
            })}
          >
            {t(detail.key, detail.params)}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={t("cashback.bannerClose")}
          onClick={() => {
            dismissBanner(data.promotionId);
            setClosedId(data.promotionId);
          }}
          sx={(theme) => ({
            color: bannerColours(theme).ink3,
            flexShrink: 0,
            p: { xs: 0.5, sm: 0.625 },
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
